import type { SimulationMesh } from './mesh';
import { dihedralAngleAndGradient } from './dihedral';
import { HINGE_KIND } from './mesh';
import { MAX_FOLD_ANGLE, clamp } from '@/constants/numeric';
import { solveSPD } from '@/geometry/linalg';

/**
 * Compliant bar-and-hinge origami solver, integrated with XPBD.
 *
 * The sheet is a triangle mesh. Three constraint families act on it:
 *
 *  1. AXIAL (bars).  C = |xi - xj| - L0.  Very stiff: paper barely stretches.
 *  2. FACET BENDING. Dihedral constraint on triangulation (auxiliary) hinges,
 *     target 0. This is what keeps a quadrilateral facet planar instead of
 *     letting it hinge along its diagonal.
 *  3. CREASE.        Dihedral constraint on real creases, target
 *     foldAmount * rho_target.
 *
 * XPBD (Macklin, Muller & Chentanez 2016) solves each constraint with
 *
 *     d lambda = ( -C - a~ lambda ) / ( sum_i w_i |grad_i C|^2 + a~ )
 *     d x_i    = w_i (grad_i C) d lambda ,      a~ = compliance / dt^2
 *
 * so stiffness is a physical compliance rather than an iteration-count
 * artefact: halving the timestep does not silently stiffen the paper.
 *
 * This is a *compliant* model on purpose. It is the right tool when the crease
 * pattern is not rigid-foldable, when you want to see where the paper is
 * strained, or when the rigid solver cannot close its loops. Use the rigid
 * solver when you need exact facet preservation.
 */

export interface CompliantParams {
  /** Global fold amount, -1 .. 1. */
  foldAmount: number;
  /** Compliance of the axial bars (m^2/N-ish; smaller = stiffer). */
  axialCompliance: number;
  /** Compliance of facet-bending (auxiliary) hinges. */
  facetCompliance: number;
  /** Compliance of crease hinges. */
  creaseCompliance: number;
  /** Velocity damping per second, 0..1. */
  damping: number;
  substeps: number;
  iterations: number;
  /** Simulated seconds per step call. */
  dt: number;
  /** Vertices pinned by the user: index -> target position. */
  pinned?: { index: number; x: number; y: number; z: number }[];
  /** Per-hinge overrides applied on top of the mesh values. */
  hingeTargetOverride?: Float64Array | null;
  hingeLockedOverride?: Uint8Array | null;
}

export const defaultCompliantParams = (): CompliantParams => ({
  foldAmount: 0,
  axialCompliance: 1e-9,
  facetCompliance: 2e-6,
  creaseCompliance: 8e-6,
  damping: 0.12,
  substeps: 6,
  iterations: 2,
  dt: 1 / 60,
});

export interface CompliantDiagnostics {
  /** Max |C| over the axial bars, relative to rest length. */
  maxAxialStrain: number;
  rmsAxialStrain: number;
  /** Max |rho - rho_target| over crease hinges, radians. */
  maxCreaseResidual: number;
  /** Max |rho| over auxiliary hinges - how far facets are from planar. */
  maxFacetBend: number;
  kineticEnergy: number;
  /** True if any coordinate went non-finite; the solver resets when this happens. */
  diverged: boolean;
  iterations: number;
}

export class CompliantSolver {
  readonly mesh: SimulationMesh;
  positions: Float64Array;
  velocities: Float64Array;
  private prev: Float64Array;
  private invMass: Float64Array;
  private lambdaBar: Float64Array;
  private lambdaHinge: Float64Array;
  private gradScratch = new Float64Array(12);
  /** Current fold angle of every hinge, refreshed each step. */
  readonly hingeAngles: Float64Array;
  /** Per-triangle max |principal strain|, refreshed by computeStrain(). */
  readonly triangleStrain: Float64Array;
  readonly vertexStrain: Float64Array;

  constructor(mesh: SimulationMesh) {
    this.mesh = mesh;
    this.positions = Float64Array.from(mesh.restPositions);
    this.velocities = new Float64Array(mesh.vertexCount * 3);
    this.prev = Float64Array.from(mesh.restPositions);
    this.invMass = new Float64Array(mesh.vertexCount);
    for (let i = 0; i < mesh.vertexCount; i++) this.invMass[i] = 1 / mesh.vertexMass[i];
    this.lambdaBar = new Float64Array(mesh.barCount);
    this.lambdaHinge = new Float64Array(mesh.hingeCount);
    this.hingeAngles = new Float64Array(mesh.hingeCount);
    this.triangleStrain = new Float64Array(mesh.triangleCount);
    this.vertexStrain = new Float64Array(mesh.vertexCount);
    this.seedOutOfPlane();
  }

  /**
   * Break the flat state's symmetry.
   *
   * A perfectly flat sheet is an equilibrium of the crease torques: every
   * gradient is in-plane, so nothing would ever leave the plane. Real paper is
   * never perfectly flat, and OrigamiSimulator solves this the same way. The
   * offset is deterministic (hash of the vertex index) so runs are repeatable.
   */
  private seedOutOfPlane(): void {
    const n = this.mesh.vertexCount;
    for (let i = 0; i < n; i++) {
      const h = Math.sin(i * 12.9898) * 43758.5453;
      this.positions[i * 3 + 2] = (h - Math.floor(h) - 0.5) * 1e-4;
    }
    this.prev.set(this.positions);
  }

  reset(): void {
    this.positions.set(this.mesh.restPositions);
    this.velocities.fill(0);
    this.lambdaBar.fill(0);
    this.lambdaHinge.fill(0);
    this.seedOutOfPlane();
  }

  /** Target angle of hinge h under the current fold amount and overrides. */
  targetAngle(h: number, params: CompliantParams): number {
    const locked =
      (params.hingeLockedOverride?.[h] ?? this.mesh.hingeLocked[h]) === 1;
    const base =
      params.hingeTargetOverride?.[h] ?? this.mesh.hingeTargetAngle[h];
    if (this.mesh.hingePatternEdge[h] < 0) return 0; // auxiliary: always planar
    if (locked) return base;
    return clamp(base * params.foldAmount, -MAX_FOLD_ANGLE, MAX_FOLD_ANGLE);
  }

  step(params: CompliantParams): CompliantDiagnostics {
    const n = this.mesh.vertexCount;
    const substeps = Math.max(1, params.substeps | 0);
    const h = params.dt / substeps;
    const dampFactor = Math.exp(-params.damping * params.dt * 60);

    for (let s = 0; s < substeps; s++) {
      // Predict.
      this.prev.set(this.positions);
      for (let i = 0; i < n * 3; i++) this.positions[i] += this.velocities[i] * h;

      // Pins are hard constraints: snap first, then let the solver work around.
      if (params.pinned) {
        for (const p of params.pinned) {
          if (p.index < 0 || p.index >= n) continue;
          this.positions[p.index * 3] = p.x;
          this.positions[p.index * 3 + 1] = p.y;
          this.positions[p.index * 3 + 2] = p.z;
        }
      }

      this.lambdaBar.fill(0);
      this.lambdaHinge.fill(0);

      const iterations = Math.max(1, params.iterations | 0);
      for (let it = 0; it < iterations; it++) {
        this.projectBars(h, params);
        this.projectHinges(h, params);
        if (params.pinned) {
          for (const p of params.pinned) {
            if (p.index < 0 || p.index >= n) continue;
            this.positions[p.index * 3] = p.x;
            this.positions[p.index * 3 + 1] = p.y;
            this.positions[p.index * 3 + 2] = p.z;
          }
        }
      }

      // Update velocities.
      const inv = 1 / h;
      for (let i = 0; i < n * 3; i++) {
        this.velocities[i] = (this.positions[i] - this.prev[i]) * inv * dampFactor;
      }
      if (!params.pinned || params.pinned.length === 0) this.removeRigidBodyMotion();
    }

    return this.diagnose(params);
  }

  private projectBars(h: number, params: CompliantParams): void {
    const { barVertices, barRestLength, barCount, barIsPatternEdge } = this.mesh;
    const pos = this.positions;
    const w = this.invMass;
    const alphaBase = params.axialCompliance / (h * h);
    for (let e = 0; e < barCount; e++) {
      const i = barVertices[e * 2];
      const j = barVertices[e * 2 + 1];
      const wi = w[i];
      const wj = w[j];
      const wsum = wi + wj;
      if (wsum <= 0) continue;
      const dx = pos[i * 3] - pos[j * 3];
      const dy = pos[i * 3 + 1] - pos[j * 3 + 1];
      const dz = pos[i * 3 + 2] - pos[j * 3 + 2];
      const len = Math.hypot(dx, dy, dz);
      if (len < 1e-12) continue;
      const C = len - barRestLength[e];
      // Triangulation diagonals are slightly softer: they are a modelling
      // device, not a physical fibre direction.
      const alpha = barIsPatternEdge[e] ? alphaBase : alphaBase * 4;
      const denom = wsum + alpha;
      const dl = (-C - alpha * this.lambdaBar[e]) / denom;
      this.lambdaBar[e] += dl;
      const s = dl / len;
      pos[i * 3] += wi * s * dx;
      pos[i * 3 + 1] += wi * s * dy;
      pos[i * 3 + 2] += wi * s * dz;
      pos[j * 3] -= wj * s * dx;
      pos[j * 3 + 1] -= wj * s * dy;
      pos[j * 3 + 2] -= wj * s * dz;
    }
  }

  private projectHinges(h: number, params: CompliantParams): void {
    const { hingeVertices, hingeCount, hingeStiffness, hingePatternEdge } = this.mesh;
    const pos = this.positions;
    const w = this.invMass;
    const g = this.gradScratch;
    for (let k = 0; k < hingeCount; k++) {
      const v1 = hingeVertices[k * 4];
      const v2 = hingeVertices[k * 4 + 1];
      const a = hingeVertices[k * 4 + 2];
      const b = hingeVertices[k * 4 + 3];
      const { angle, grad } = dihedralAngleAndGradient(pos, v1, v2, a, b, g);
      this.hingeAngles[k] = angle;
      const target = this.targetAngle(k, params);
      const C = angle - target;
      if (C === 0) continue;

      const isCrease = hingePatternEdge[k] >= 0;
      const baseCompliance = isCrease ? params.creaseCompliance : params.facetCompliance;
      const stiff = Math.max(1e-6, hingeStiffness[k]);
      const alpha = baseCompliance / stiff / (h * h);

      const idx = [v1, v2, a, b];
      let denom = alpha;
      for (let q = 0; q < 4; q++) {
        const wq = w[idx[q]];
        if (wq <= 0) continue;
        const gx = grad[q * 3];
        const gy = grad[q * 3 + 1];
        const gz = grad[q * 3 + 2];
        denom += wq * (gx * gx + gy * gy + gz * gz);
      }
      if (!(denom > 1e-18) || !Number.isFinite(denom)) continue;
      const dl = (-C - alpha * this.lambdaHinge[k]) / denom;
      if (!Number.isFinite(dl)) continue;
      this.lambdaHinge[k] += dl;
      for (let q = 0; q < 4; q++) {
        const vi = idx[q];
        const wq = w[vi];
        if (wq <= 0) continue;
        pos[vi * 3] += wq * dl * grad[q * 3];
        pos[vi * 3 + 1] += wq * dl * grad[q * 3 + 1];
        pos[vi * 3 + 2] += wq * dl * grad[q * 3 + 2];
      }
    }
  }

  /**
   * Project the net rigid-body motion out of the velocity field.
   *
   * Every constraint here is invariant under rigid motions, so the sheet has six
   * free modes and nothing stops it drifting and tumbling as it folds - which
   * leaves an unfolded sheet sitting flat but at some arbitrary angle. Working in
   * the centre-of-mass frame removes the three translations, and subtracting
   * omega x r with omega = I^-1 L removes the three rotations. This changes no
   * relative motion, so the folding itself is untouched.
   *
   * Skipped while the user is dragging a vertex: then the pin *is* the anchor,
   * and removing rigid-body motion would fight it.
   */
  private removeRigidBodyMotion(): void {
    const n = this.mesh.vertexCount;
    if (n < 3) return;
    const mass = this.mesh.vertexMass;
    const pos = this.positions;
    const vel = this.velocities;

    let total = 0;
    let cx = 0, cy = 0, cz = 0;
    let px = 0, py = 0, pz = 0;
    for (let i = 0; i < n; i++) {
      const m = mass[i];
      total += m;
      cx += m * pos[i * 3];
      cy += m * pos[i * 3 + 1];
      cz += m * pos[i * 3 + 2];
      px += m * vel[i * 3];
      py += m * vel[i * 3 + 1];
      pz += m * vel[i * 3 + 2];
    }
    if (!(total > 0)) return;
    cx /= total; cy /= total; cz /= total;
    const vx = px / total, vy = py / total, vz = pz / total;

    // Angular momentum and inertia tensor about the centre of mass.
    let lx = 0, ly = 0, lz = 0;
    const I = new Float64Array(9);
    for (let i = 0; i < n; i++) {
      const m = mass[i];
      const rx = pos[i * 3] - cx;
      const ry = pos[i * 3 + 1] - cy;
      const rz = pos[i * 3 + 2] - cz;
      const wx = vel[i * 3] - vx;
      const wy = vel[i * 3 + 1] - vy;
      const wz = vel[i * 3 + 2] - vz;
      lx += m * (ry * wz - rz * wy);
      ly += m * (rz * wx - rx * wz);
      lz += m * (rx * wy - ry * wx);
      const r2 = rx * rx + ry * ry + rz * rz;
      I[0] += m * (r2 - rx * rx);
      I[1] -= m * rx * ry;
      I[2] -= m * rx * rz;
      I[4] += m * (r2 - ry * ry);
      I[5] -= m * ry * rz;
      I[8] += m * (r2 - rz * rz);
    }
    I[3] = I[1];
    I[6] = I[2];
    I[7] = I[5];

    const omega = solveSPD(
      { rows: 3, cols: 3, data: I },
      Float64Array.from([lx, ly, lz]),
      1e-12,
    );

    for (let i = 0; i < n; i++) {
      const rx = pos[i * 3] - cx;
      const ry = pos[i * 3 + 1] - cy;
      const rz = pos[i * 3 + 2] - cz;
      let ox = 0, oy = 0, oz = 0;
      if (omega) {
        ox = omega[1] * rz - omega[2] * ry;
        oy = omega[2] * rx - omega[0] * rz;
        oz = omega[0] * ry - omega[1] * rx;
      }
      vel[i * 3] -= vx + ox;
      vel[i * 3 + 1] -= vy + oy;
      vel[i * 3 + 2] -= vz + oz;
    }
  }

  private diagnose(params: CompliantParams): CompliantDiagnostics {
    const pos = this.positions;
    let maxAxial = 0;
    let sumSq = 0;
    for (let e = 0; e < this.mesh.barCount; e++) {
      const i = this.mesh.barVertices[e * 2];
      const j = this.mesh.barVertices[e * 2 + 1];
      const len = Math.hypot(
        pos[i * 3] - pos[j * 3],
        pos[i * 3 + 1] - pos[j * 3 + 1],
        pos[i * 3 + 2] - pos[j * 3 + 2],
      );
      const L0 = this.mesh.barRestLength[e];
      const strain = L0 > 0 ? (len - L0) / L0 : 0;
      maxAxial = Math.max(maxAxial, Math.abs(strain));
      sumSq += strain * strain;
    }

    let maxCrease = 0;
    let maxFacet = 0;
    for (let k = 0; k < this.mesh.hingeCount; k++) {
      const angle = this.hingeAngles[k];
      if (this.mesh.hingePatternEdge[k] >= 0) {
        maxCrease = Math.max(maxCrease, Math.abs(angle - this.targetAngle(k, params)));
      } else {
        maxFacet = Math.max(maxFacet, Math.abs(angle));
      }
    }

    let ke = 0;
    for (let i = 0; i < this.mesh.vertexCount; i++) {
      const vx = this.velocities[i * 3];
      const vy = this.velocities[i * 3 + 1];
      const vz = this.velocities[i * 3 + 2];
      ke += 0.5 * this.mesh.vertexMass[i] * (vx * vx + vy * vy + vz * vz);
    }

    let diverged = false;
    for (let i = 0; i < pos.length; i++) {
      if (!Number.isFinite(pos[i])) {
        diverged = true;
        break;
      }
    }
    if (diverged) this.reset();

    return {
      maxAxialStrain: maxAxial,
      rmsAxialStrain: this.mesh.barCount ? Math.sqrt(sumSq / this.mesh.barCount) : 0,
      maxCreaseResidual: maxCrease,
      maxFacetBend: maxFacet,
      kineticEnergy: ke,
      diverged,
      iterations: params.substeps * params.iterations,
    };
  }

  /**
   * Per-triangle strain: the larger principal value of the Green-Lagrange
   * strain tensor E = (F^T F - I)/2, computed in each triangle's own rest
   * frame. Dimensionless, so "2%" means what it says regardless of paper size.
   */
  computeStrain(): void {
    const { triangles, triangleCount, restPositions } = this.mesh;
    const pos = this.positions;
    const counts = new Float64Array(this.mesh.vertexCount);
    this.vertexStrain.fill(0);

    for (let t = 0; t < triangleCount; t++) {
      const i = triangles[t * 3];
      const j = triangles[t * 3 + 1];
      const k = triangles[t * 3 + 2];

      // Rest frame (the pattern is planar, so use xy directly).
      const r1x = restPositions[j * 3] - restPositions[i * 3];
      const r1y = restPositions[j * 3 + 1] - restPositions[i * 3 + 1];
      const r2x = restPositions[k * 3] - restPositions[i * 3];
      const r2y = restPositions[k * 3 + 1] - restPositions[i * 3 + 1];
      const det = r1x * r2y - r1y * r2x;
      if (Math.abs(det) < 1e-16) {
        this.triangleStrain[t] = 0;
        continue;
      }
      // Inverse of the rest edge matrix [r1 r2].
      const inv00 = r2y / det, inv01 = -r2x / det;
      const inv10 = -r1y / det, inv11 = r1x / det;

      const d1x = pos[j * 3] - pos[i * 3];
      const d1y = pos[j * 3 + 1] - pos[i * 3 + 1];
      const d1z = pos[j * 3 + 2] - pos[i * 3 + 2];
      const d2x = pos[k * 3] - pos[i * 3];
      const d2y = pos[k * 3 + 1] - pos[i * 3 + 1];
      const d2z = pos[k * 3 + 2] - pos[i * 3 + 2];

      // F (3x2) = [d1 d2] * inv
      const f00 = d1x * inv00 + d2x * inv10;
      const f01 = d1x * inv01 + d2x * inv11;
      const f10 = d1y * inv00 + d2y * inv10;
      const f11 = d1y * inv01 + d2y * inv11;
      const f20 = d1z * inv00 + d2z * inv10;
      const f21 = d1z * inv01 + d2z * inv11;

      // C = F^T F (2x2 symmetric)
      const c00 = f00 * f00 + f10 * f10 + f20 * f20;
      const c01 = f00 * f01 + f10 * f11 + f20 * f21;
      const c11 = f01 * f01 + f11 * f11 + f21 * f21;

      const e00 = (c00 - 1) / 2;
      const e01 = c01 / 2;
      const e11 = (c11 - 1) / 2;

      const tr = e00 + e11;
      const disc = Math.sqrt(Math.max(0, ((e00 - e11) / 2) ** 2 + e01 * e01));
      const l1 = tr / 2 + disc;
      const l2 = tr / 2 - disc;
      const strain = Math.max(Math.abs(l1), Math.abs(l2));
      this.triangleStrain[t] = strain;

      this.vertexStrain[i] += strain;
      this.vertexStrain[j] += strain;
      this.vertexStrain[k] += strain;
      counts[i]++;
      counts[j]++;
      counts[k]++;
    }
    for (let i = 0; i < this.mesh.vertexCount; i++) {
      if (counts[i] > 0) this.vertexStrain[i] /= counts[i];
    }
  }

  /** Which crease assignment does hinge k carry? */
  hingeAssignmentOf(k: number): string {
    return HINGE_KIND[this.mesh.hingeAssignment[k]] ?? 'J';
  }
}
